import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { EWidgetRegion, type EWidgetSection } from '#shared';

import type { RootState } from '../redux-store';

export type CssSize = `${number}fr` | `${number}px` | `${number}%` | 'auto';

// Section limits per region
export const SECTION_LIMITS = {
  MIN: 1,
  MAX: 4,
  CENTER_MAX: 1, // Center region allows only 1 section
} as const;

export interface RegionLayout {
  id: EWidgetRegion;
  sectionOrder: EWidgetSection[];
  sectionSizes: Partial<Record<EWidgetSection, CssSize>>;
  hidden?: boolean;
  enabled: boolean;
}

export type RegionManagerState = RegionLayout[];

export const regionSlice = createSlice({
  name: 'layout/regions',
  initialState: [] as RegionManagerState,
  reducers: {
    regionEnable(
      state,
      {
        payload,
      }: PayloadAction<{
        regionId: EWidgetRegion | EWidgetRegion[];
        only?: boolean;
        maxRegions?: number;
        preferredOrder?: EWidgetRegion[];
      }>,
    ) {
      const regionIds = Array.isArray(payload.regionId)
        ? payload.regionId
        : [payload.regionId];

      // Apply maxRegions limit with preference support
      let targetIds = regionIds;
      if (payload.maxRegions && regionIds.length > payload.maxRegions) {
        if (payload.preferredOrder) {
          const filtered = payload.preferredOrder.filter(id =>
            regionIds.includes(id),
          );
          targetIds = filtered.slice(0, payload.maxRegions);
        } else {
          targetIds = regionIds.slice(0, payload.maxRegions);
        }
      }

      // Enable/disable regions
      state.forEach(region => {
        if (targetIds.includes(region.id)) {
          region.enabled = true;
        } else if (payload.only) {
          region.enabled = false;
        }
      });

      // Ensure at least one region is enabled (fallback to Center)
      const hasEnabled = state.some(r => r.enabled);
      if (!hasEnabled && state.length > 0) {
        const centerRegion = state.find(r => r.id === EWidgetRegion.Center);
        const fallback = centerRegion ?? state[0];
        fallback.enabled = true;
      }
    },

    regionDisable(
      state,
      {
        payload,
      }: PayloadAction<{
        regionId: EWidgetRegion | EWidgetRegion[];
        only?: boolean;
      }>,
    ) {
      const regionIds = Array.isArray(payload.regionId)
        ? payload.regionId
        : [payload.regionId];
      const enabledRegions = state.filter(r => r.enabled);
      const enabledCount = enabledRegions.length;

      state.forEach(region => {
        const shouldDisable = regionIds.includes(region.id) && enabledCount > 1;

        if (shouldDisable) {
          region.enabled = false;
        } else if (payload.only) {
          region.enabled = true;
        }
      });
    },

    regionShow(
      state,
      {
        payload,
      }: PayloadAction<{
        regionId: EWidgetRegion | EWidgetRegion[];
        only?: boolean;
      }>,
    ) {
      const regionIds = Array.isArray(payload.regionId)
        ? payload.regionId
        : [payload.regionId];

      state.forEach(region => {
        if (regionIds.includes(region.id)) {
          region.hidden = false;
        } else if (payload.only) {
          region.hidden = true;
        }
      });
    },

    regionHide(
      state,
      {
        payload,
      }: PayloadAction<{
        regionId: EWidgetRegion | EWidgetRegion[];
        only?: boolean;
      }>,
    ) {
      const regionIds = Array.isArray(payload.regionId)
        ? payload.regionId
        : [payload.regionId];

      state.forEach(region => {
        if (regionIds.includes(region.id)) {
          region.hidden = true;
        } else if (payload.only) {
          region.hidden = false;
        }
      });
    },

    /**
     * Replace entire regions state (used for template application)
     */
    setRegions(state, action: PayloadAction<RegionLayout[]>) {
      return action.payload;
    },

    /**
     * Add a section to a region at a specific position
     * Enforces min/max section limits (1-4 for left/right, 1 for center)
     */
    regionAddSection(
      state,
      {
        payload,
      }: PayloadAction<{
        regionId: EWidgetRegion;
        sectionId: EWidgetSection;
        position: number;
        size?: CssSize;
      }>,
    ) {
      const region = state.find(r => r.id === payload.regionId);
      if (!region) {
        return;
      }

      // Check max limit based on region type
      const maxSections =
        payload.regionId === EWidgetRegion.Center
          ? SECTION_LIMITS.CENTER_MAX
          : SECTION_LIMITS.MAX;

      if (region.sectionOrder.length >= maxSections) {
        return; // Cannot add more sections
      }

      // Prevent duplicates
      if (region.sectionOrder.includes(payload.sectionId)) {
        return;
      }

      // Insert at position (clamp to valid range)
      const insertPosition = Math.max(
        0,
        Math.min(payload.position, region.sectionOrder.length),
      );
      region.sectionOrder.splice(insertPosition, 0, payload.sectionId);
      region.sectionSizes[payload.sectionId] = payload.size ?? '1fr';
    },

    /**
     * Remove a section from a region
     * Prevents removal if it would violate min section limit (1)
     */
    regionRemoveSection(
      state,
      {
        payload,
      }: PayloadAction<{
        regionId: EWidgetRegion;
        sectionId: EWidgetSection;
      }>,
    ) {
      const region = state.find(r => r.id === payload.regionId);
      if (!region) {
        return;
      }

      // Prevent removal if at minimum
      if (region.sectionOrder.length <= SECTION_LIMITS.MIN) {
        return;
      }

      // Remove from order and sizes
      region.sectionOrder = region.sectionOrder.filter(
        id => id !== payload.sectionId,
      );

      const { [payload.sectionId]: _, ...restSizes } = region.sectionSizes;
      region.sectionSizes = restSizes;
    },
  },
});

const selectRegionsEnabled = createSelector(
  [(state: RootState) => state.regions],
  regions => regions.filter(r => r.enabled),
);

const selectRegionsHidden = createSelector(
  [(state: RootState) => state.regions],
  regions => regions.filter(r => r.hidden),
);

const selectRegionsActive = createSelector(
  [(state: RootState) => state.regions],
  regions => regions.filter(r => r.enabled && !r.hidden),
);

const selectRegionTemplateColumns = (regionId: EWidgetRegion) =>
  createSelector(
    [(state: RootState) => state.regions, (state: RootState) => state.sections],
    (regions, sections) => {
      const r = regions.find(region => region.id === regionId);
      if (!r) {
        return '';
      }
      const enabledIds = Object.values(sections.entities)
        .filter(
          section => r.sectionOrder.includes(section.id) && !section.disabled,
        )
        .map(section => section.id);
      return r.sectionOrder
        .filter(id => enabledIds.includes(id))
        .map(id => r.sectionSizes[id] ?? '1fr')
        .join(' ');
    },
  );
export const useSelectRegions = () => useSelector((s: RootState) => s.regions);
export const useSelectRegionsEnabled = () => useSelector(selectRegionsEnabled);
export const useSelectRegionsHidden = () => useSelector(selectRegionsHidden);

export const useSelectRegionsActive = () => useSelector(selectRegionsActive);
export const useSelectRegionById = (regionId: EWidgetRegion) =>
  useSelector((s: RootState) =>
    s.regions.find(region => region.id === regionId),
  );
export const useSelectRegionSectionOrder = (regionId: EWidgetRegion) =>
  useSelector(
    (s: RootState) =>
      s.regions.find(region => region.id === regionId)?.sectionOrder ?? [],
  );
export const useSelectRegionSectionSizes = (regionId: EWidgetRegion) =>
  useSelector(
    (s: RootState) =>
      s.regions.find(region => region.id === regionId)?.sectionSizes ?? {},
  );
export const useSelectRegionTemplateColumns = (regionId: EWidgetRegion) => {
  const regionTemplateColumnsSelector = useMemo(
    () => selectRegionTemplateColumns(regionId),
    [regionId],
  );
  return useSelector(regionTemplateColumnsSelector);
};

/**
 * Check if a region can add more sections
 */
export const useSelectCanAddSection = (regionId: EWidgetRegion) =>
  useSelector((s: RootState) => {
    const region = s.regions.find(r => r.id === regionId);
    if (!region) {
      return false;
    }
    const maxSections =
      regionId === EWidgetRegion.Center
        ? SECTION_LIMITS.CENTER_MAX
        : SECTION_LIMITS.MAX;
    return region.sectionOrder.length < maxSections;
  });

/**
 * Check if a region can remove sections
 */
export const useSelectCanRemoveSection = (regionId: EWidgetRegion) =>
  useSelector((s: RootState) => {
    const region = s.regions.find(r => r.id === regionId);
    if (!region) {
      return false;
    }
    return region.sectionOrder.length > SECTION_LIMITS.MIN;
  });

/**
 * Get the current section count for a region
 */
export const useSelectRegionSectionCount = (regionId: EWidgetRegion) =>
  useSelector(
    (s: RootState) =>
      s.regions.find(r => r.id === regionId)?.sectionOrder.length ?? 0,
  );

export const regionActions = regionSlice.actions;
